import { FC, useMemo } from 'react';

import { useTour } from '@reactour/tour';
import { inflect } from 'inflection';
import {
  Calendar,
  type ViewsProps,
  luxonLocalizer,
  Formats,
  CalendarProps,
  View,
} from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button, Tooltip } from 'ui/components/common';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import { cn } from 'utils/helpers';
import { DEMO_EVENT, EVENT_TABS, EVENT_TYPES, SIZE } from 'utils/constants';
import { Event } from 'utils/types';
import { DateTime, Settings } from 'luxon';
import { useTimezone } from 'utils/providers/timezone.provider';
import { Tabs } from 'ui/components/nav';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6';
import dayjs, { OpUnitType } from 'dayjs';

import getTypeColor from '../utils/getTypeColor';
import { useEventContext } from '../utils/providers/EventContext';
import { useCalendarContext } from '../utils/providers/CalendarContext';

const formats: Formats = {
  timeGutterFormat: 'h a',
  dayHeaderFormat: 'cccc, LLL d',
};

interface MONTH_PROPS {
  startAt: string;
  title: string;
  border: string;
  theme?: 'light' | 'dark';
  hoverable?: boolean;
  onClick?: () => void;
  type: EVENT_TYPES;
}

function toGMTLabel(zone: string) {
  const dt = DateTime.now().setZone(zone);
  const offsetHours = dt.offset / 60; // offset is in minutes

  const sign = offsetHours >= 0 ? '+' : '-';
  const padded = String(Math.abs(offsetHours)).padStart(2, '0');

  return `GMT${sign}${padded}`;
}

const MonthAccessor: FC<MONTH_PROPS> = ({
  title,
  border,
  startAt,
  theme = 'light',
  hoverable,
  onClick,
  type,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      theme === 'light' ? 'bg-fill-tertiary' : 'bg-secondary-base',
      border,
      'border-transparent',
      'flex items-center gap-x-3',
      'w-full rounded-[6px] p-1',
      hoverable && 'text-white hover:underline ',
    )}
  >
    <div
      className={cn(
        'h-1 w-1 flex-shrink-0 rounded-full',
        getTypeColor(type, EVENT_TABS.MONTH),
      )}
    />
    <span
      className={cn(
        'w-8 text-left text-[10px] font-semibold leading-none whitespace-nowrap',
        theme === 'light' && 'text-secondary-base',
        theme === 'dark' && 'text-fill-tertiary',
      )}
    >
      {DateTime.fromJSDate(new Date(startAt)).toFormat('h:mm a')}
    </span>
    <span
      className={cn(
        'w-full text-left leading-none',
        'truncate text-[10px] font-semibold',
        theme === 'light' && 'text-secondary-base',
        theme === 'dark' && 'text-fill-tertiary',
      )}
    >
      {title}
    </span>
  </button>
);

type PROPS = {
  handleNavigate: CalendarProps['onNavigate'];
} & Pick<
  CalendarProps,
  | 'defaultView'
  | 'defaultDate'
  | 'view'
  | 'onView'
  | 'date'
  | 'onDrillDown'
  | 'onSelectSlot'
>;

const BigCalendar: FC<PROPS> = ({
  handleNavigate,
  date,
  defaultDate,
  defaultView,
  view = 'month',
  onView,
  onDrillDown,
  onSelectSlot,
}) => {
  const { setSelectedEventId } = useEventContext();
  const { data, tabs } = useCalendarContext()!;
  const { isOpen } = useTour();

  const state = useTimezone();

  const { getNow, localizer, events, scrollToTime } = useMemo(() => {
    Settings.defaultZone = state.localTimezone;

    return {
      getNow: () => DateTime.local().toJSDate(),
      localizer: luxonLocalizer(DateTime),
      events: isOpen ? ([DEMO_EVENT] as unknown as Event[]) : data ?? [],
      scrollToTime: DateTime.local().toJSDate(),
    };
  }, [state.localTimezone, data, isOpen]);

  const getBorder = useMemo(() => {
    if (view === 'week') {
      return 'border-t-2';
    } else {
      return 'border-l-2';
    }
  }, [view]);

  const handleTitleAccessor = ({
    title,
    startAt,
    endAt,
    type,
  }: Event): string => {
    const { localTimezone } = state;

    const formatDateTime = (date: string) => {
      return DateTime.fromJSDate(new Date(date))
        .setZone(localTimezone)
        .toFormat('H:mm');
    };
    switch (view.toUpperCase()) {
      case EVENT_TABS.MONTH:
        return (
          <Tooltip
            className="w-full"
            title={
              <MonthAccessor
                border={getBorder}
                title={title}
                startAt={startAt}
                type={type}
              />
            }
          >
            <MonthAccessor
              border={getBorder}
              title={title}
              startAt={startAt}
              type={type}
            />
          </Tooltip>
        ) as unknown as string;

      case EVENT_TABS.DAY:
        return (
          <div
            data-tut={TOUR_SELECTOR.TOUR.CALENDAR.CALENDAR.MANAGE.DETAILS}
            className={cn(
              'bg-fill-tertiary',
              getBorder,
              getTypeColor(type, EVENT_TABS.DAY),
              view !== 'day' && 'mx-2',
            )}
          >
            <span className="truncate text-[10px] font-semibold leading-none">
              {title}
            </span>
            <span className="truncate text-[10px] leading-none text-content">
              {formatDateTime(startAt)} - {formatDateTime(endAt)}
            </span>
          </div>
        ) as unknown as string;
      default:
        return (
          <div
            className={cn(
              'flex-col items-start bg-fill-tertiary',
              getBorder,
              getTypeColor(type, EVENT_TABS.DAY),
              view !== 'day' && 'mx-2',
            )}
          >
            <span
              className={cn(
                'text-[10px] font-semibold leading-none',
                'flex-shrink-0',
                'w-full truncate',
              )}
            >
              {title}
            </span>
            <span className="w-full flex-shrink-0 truncate text-[10px] leading-none text-content">
              {formatDateTime(startAt)} - {formatDateTime(endAt)}
            </span>
          </div>
        ) as unknown as string;
    }
  };

  return (
    <Calendar
      onRangeChange={(range) => console.log({ range })}
      defaultDate={defaultDate}
      defaultView={defaultView}
      localizer={localizer}
      events={events}
      startAccessor="startAt"
      endAccessor="endAt"
      getNow={getNow}
      drilldownView={'day'}
      scrollToTime={scrollToTime}
      views={
        tabs.map((t: string) => t.toLowerCase()) as ViewsProps<Event, object>
      }
      components={{
        toolbar: ({ views, onView, label, onNavigate, view, date }) => {
          const startOfByView = dayjs(date)
            .tz(state.localTimezone)
            .startOf(view as OpUnitType)
            .toDate();
          return (
            <div
              className={cn(
                'flex justify-between items-center',
                'pb-[20px] pt-[18px]',
              )}
            >
              <div className="flex items-center gap-[22px]">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="p-1 transition group hover:bg-secondary-lighter"
                    onClick={() => {
                      onNavigate('PREV', startOfByView);
                    }}
                  >
                    <FaChevronLeft className="w-5 h-5 group-hover:text-primary-base" />
                  </button>
                  <Button
                    label="TODAY"
                    intent={'text'}
                    className="p-1 transition group hover:bg-secondary-lighter"
                    onClick={() => {
                      handleNavigate &&
                        handleNavigate?.(
                          DateTime.now().toJSDate(),
                          'day',
                          'TODAY',
                        );
                    }}
                  />

                  <button
                    type="button"
                    className="p-1 transition group hover:bg-secondary-lighter"
                    onClick={() => {
                      onNavigate('NEXT', startOfByView);
                    }}
                  >
                    <FaChevronRight className="w-5 h-5 group-hover:text-primary-base" />
                  </button>
                </div>
                <p className="text-sm font-semibold">{label}</p>
              </div>

              <div className="flex gap-x-[11px]">
                <Tabs
                  selectedTab={view}
                  items={(views as View[]).map((tab) => ({
                    label: tab.toUpperCase(),
                    value: tab,
                  }))}
                  size={SIZE.Small}
                  type="tertiary"
                  onChange={({ value: tab }) => onView(tab as View)}
                />
              </div>
            </div>
          );
        },
        timeGutterHeader: () => {
          if (
            toGMTLabel(state.accountTimezone) ===
            toGMTLabel(state.localTimezone)
          )
            return null;
          return (
            <div
              className={cn(
                'text-[10px] text-content flex gap-5 items-center ',
                'ml-1 mb-6',
                view === 'week' && 'absolute ml-1 mt-6 ',
              )}
            >
              <Tooltip theme={'dark'} title={state.accountTimezone}>
                {toGMTLabel(state.accountTimezone)}
              </Tooltip>
              <Tooltip theme={'dark'} title={state.localTimezone}>
                {toGMTLabel(state.localTimezone)}
              </Tooltip>
            </div>
          );
        },
        timeGutterWrapper:
          toGMTLabel(state.accountTimezone) === toGMTLabel(state.localTimezone)
            ? undefined
            : TimeGutterWrapper,
        showMore: ({ count, remainingEvents }) => {
          return (remainingEvents.length ? (
            <Tooltip
              mainAxisOffset={0}
              theme="dark"
              title={
                <div className="space-y-2">
                  {remainingEvents.map((event) => {
                    return (
                      <MonthAccessor
                        key={event?._id}
                        onClick={() => setSelectedEventId?.(event._id)}
                        hoverable
                        theme="dark"
                        border="border-l-2"
                        startAt={event?.startAt}
                        title={event?.title}
                        type={event?.type}
                      />
                    );
                  })}
                </div>
              }
            >
              <p className="ml-4 mt-1 text-[10px] text-content">{`${count} more ${inflect(
                'event',
                count,
              )}`}</p>
            </Tooltip>
          ) : (
            <p className="ml-4 mt-1 text-[10px] text-content">{`${count} more ${inflect(
              'event',
              count,
            )}`}</p>
          )) as unknown as string;
        },
      }}
      date={date}
      onDrillDown={onDrillDown}
      onNavigate={handleNavigate}
      onView={onView}
      titleAccessor={handleTitleAccessor}
      formats={formats}
      step={30}
      view={view}
      selectable="ignoreEvents"
      onSelectSlot={onSelectSlot}
      onSelectEvent={(e) => setSelectedEventId?.(e._id)}
    />
  );
};

export default BigCalendar;

const TimeGutterWrapper = (props: any) => {
  const state = useTimezone();

  const zone = state.accountTimezone;

  const min = DateTime.fromObject({ hour: 0 }).setZone(zone);
  const max = DateTime.fromObject({ hour: 23, minute: 59 }).setZone(zone);

  const slots = [];
  let current = min;

  while (current < max) {
    slots.push(current);
    current = current.plus({ hours: 1 });
  }

  return (
    <div className="relative flex justify-center">
      <div className="flex">
        {/* ACCOUNT TIME ZONE */}
        <div>
          <div className="rbc-time-gutter rbc-time-column">
            {slots.map((slot, i) => (
              <div key={i} className="rbc-timeslot-group">
                <div className="rbc-time-slot">
                  <span className="rbc-label">
                    {slot.setZone(zone).toFormat('h a')}
                  </span>
                </div>
                <div className="rbc-time-slot"></div>
              </div>
            ))}
          </div>
        </div>
        {/* LOCAL TIME ZONE */}
        <div>{props.children}</div>
      </div>
    </div>
  );
};
